import { useEffect } from 'react';
import {
  AnyAction,
  createAsyncThunk,
  PayloadAction,
  ThunkDispatch,
} from '@reduxjs/toolkit';
import type { AppState } from 'shared/store/reducers';
import type { RealtimeState } from 'shared/store/realtime-slice';
import { Trigger } from 'types';
import { startOutgoingDisplayStream, stopOutgoingDisplayStream } from './manager';
import { useAppDispatch, useAppSelector } from 'shared/store';
import { makeLog } from 'shared/utils/log';
import { createKit } from './kit';
import { selectActiveVisitor } from 'shared/store/shared-selectors';
import { useUpdatingRef } from 'shared/hooks';

type PA<T> = PayloadAction<T>;

const log = makeLog('displaydemo-kit');

/**
 * Displaydemo flow rough outline
 * 1) Admin starts is_displaydemo interaction and sends display stream over p2p
 * 2) Widget receives displaystream and immediately starts rendering it as a video
 */

/****************************************************
 *
 *                 Thunks
 *
 ****************************************************/
export const startDisplaydemo = createAsyncThunk<void, void, { state: AppState }>(
  'realtime/startDisplaydemo',
  async (_, { getState }) => {
    if (getState().realtime.outgoingDisplayStreamId) {
      throw new Error('Already screensharing');
    }
    await startOutgoingDisplayStream();
  },
);

export const endDisplaydemo = createAsyncThunk<void, void>(
  'realtime/endDisplaydemo',
  async () => {
    await stopOutgoingDisplayStream();
  },
);

/****************************************************
 *
 *                 Reducers
 *
 ****************************************************/
const reducers = {
  outgoingDisplayStreamChanged: (state: RealtimeState, action: PA<string | null>) => {
    state.outgoingDisplayStreamId = action.payload || null;
  },
};

// Access actions before createSlice so we can reference them locally
// const actions = createRealtimeReducerActions({ reducers });

export const displaydemoKit = createKit({
  reducers,
  extraReducers: builder => {
    builder.addCase(startDisplaydemo.fulfilled, state => {
      state.optimisticInteractions = {
        ...state.optimisticInteractions,
        isDisplaydemo: true,
      };
    });
    builder.addCase(endDisplaydemo.fulfilled, state => {
      state.outgoingDisplayStreamId = null;
      state.optimisticInteractions = {
        ...state.optimisticInteractions,
        isDisplaydemo: false,
      };
    });
  },

  /****************************************************
   *
   *               Handle triggers
   *
   ****************************************************/
  triggerThunkHelper: (
    trigger: Trigger,
    _: any,
    dispatch: ThunkDispatch<any, AnyAction, any>,
  ) => {
    if (trigger === Trigger.REJECT_CALL || trigger === Trigger.STOP_DISPLAYDEMO) {
      dispatch(endDisplaydemo());
    }
  },

  /****************************************************
   *
   *         Visitor sync/update reducer helpers
   *
   ****************************************************/
  visitorUpdateReducer: () => undefined,

  /****************************************************
   *
   *               Kit hook
   *
   ****************************************************/
  useKitHook: () => {
    const dispatch = useAppDispatch();
    const activeVisitor = useAppSelector(selectActiveVisitor);
    const { outgoingDisplayStreamId } = useAppSelector(s => s.realtime);
    const displayStreamIdRef = useUpdatingRef(outgoingDisplayStreamId);
    const isVideo = !!activeVisitor?.isVideo;

    // Uses a ref for outgoingDisplayStreamId and simplifies isVideo so that
    // this effect only triggers on call state transitions.
    useEffect(
      function stopDisplaydemoOnCallEndEffect() {
        if (!isVideo && displayStreamIdRef.current) {
          log.debug('Stopping displaydemo due to call ending while demoing');
          dispatch(endDisplaydemo());
        }
      },
      [isVideo, displayStreamIdRef, dispatch],
    );
  },
});
